import { z } from 'zod/v4';

import { foldSynonyms, isNumber, isString, normalizeFlatArgs } from './fold-helpers';
import { WIDGET_ID_SYNONYM_KEYS } from './remove-widget.schema';

/**
 * `position` is what a model naturally calls a list index. `newIndex`/`toIndex`/`to` show up when it
 * thinks of the move as from→to. Deliberately NOT aliased: `order` (ambiguous between an index and a
 * sort direction) and `previousIndex`/`from` (the server reads the current position itself — letting
 * a model assert it invites a wrong value silently reordering the wrong entity). Shared with the
 * column/block reorder schemas, which have the exact same ambiguity.
 */
export const INDEX_SYNONYM_KEYS = ['position', 'newIndex', 'toIndex', 'to'] as const;

const COLUMN_ID_SYNONYM_KEYS = ['targetColumnId', 'column_id'] as const;

const reorderWidgetObjectSchema = z.object({
    widgetId: z.string().describe('Id of the widget to move.'),
    index: z
        .number()
        .int()
        .min(0)
        .describe(
            'Destination position in the target column (0-based, counted with this widget already taken out ' +
                'of the list). Larger than the list is clamped to the end.'
        ),
    columnId: z
        .string()
        .optional()
        .describe('Move the widget into this column. Omit to reposition it within the column it is already in.'),
});

export const reorderWidgetArgsSchema = z.preprocess(
    (raw) =>
        normalizeFlatArgs(raw, (args) => {
            foldSynonyms(args, args, 'widgetId', WIDGET_ID_SYNONYM_KEYS, isString);
            foldSynonyms(args, args, 'index', INDEX_SYNONYM_KEYS, isNumber);
            foldSynonyms(args, args, 'columnId', COLUMN_ID_SYNONYM_KEYS, isString);
        }),
    reorderWidgetObjectSchema
);
